/**
 * Shared inset glow: owner-tinted source, white attack/fortify targets.
 *
 * Static on purpose. MapView draws it in the selection overlay, which fades as
 * one native view (MapOverlayLayer). It must hold no reanimated component:
 * reanimated re-applies a mounted animated component's props on every React
 * commit anywhere in the app, and react-native-svg treats any props update as
 * a change and redraws the whole <Svg>. On Arctic each redraw of these glows
 * costs ~40-60ms, so an idle animated group here repainted them after every
 * game-state update while a conquest played out (docs/map-perf.md).
 */
import React from 'react'
import { Circle, Defs, G, Path } from 'react-native-svg'
import { territoryMarkerSelectionRing } from './markers/geometry'
import { TerritoryInteriorMask } from './TerritoryInteriorMask'

// Wide, faint strokes first; the narrow opaque core sits at the inset edge.
// Compositing white with decreasing alpha reveals the actual underlying fill.
const INNER_GLOW_PASSES = Array.from({ length: 8 }, (_, index) => {
  const depth = 1 - index * 0.75 / 7
  return { depth, opacity: index === 7 ? 1 : 0.16 }
})

export interface TargetHaloLayerProps {
  /** CTF's compact target-only markers when inner glow is off. */
  targetCircles?: {
    positionById: Record<string, { x: number; y: number } | undefined>
    radius: number
  }
  markerCircles?: { positionById: Record<string, { x: number; y: number } | undefined>; radius: number }
  territoryIds: readonly string[]
  settledTerritoryIds?: readonly string[]
  sourceTerritoryId?: string
  sourceColor?: string
  territoryShapeById: Record<string, string | undefined>
  territoryRegionShapeById: Record<string, string | undefined>
  /** Per-map namespace, also shared with the territory clips in MapView. */
  clipNS: string
  /** Visible inward half-width of the ordinary territory border. */
  borderInset: number
  /** Visible inward half-width on edges of the territory's region. */
  regionBorderInset: number
  /** Visible inward falloff, independent of border width, in viewBox units. */
  glowWidth: number
}

interface InnerGlowProps {
  id: string
  shape: string
  regionShape?: string
  clipNS: string
  borderInset: number
  regionBorderInset: number
  glowWidth: number
  color: string
}

function InnerGlow({ id, shape, regionShape, clipNS, borderInset, regionBorderInset, glowWidth, color }: InnerGlowProps) {
  const maskId = `${clipNS}target-inset-${id}`
  return (
    <G pointerEvents="none">
      <Defs>
        <TerritoryInteriorMask id={maskId} shape={shape} regionShape={regionShape}
          borderInset={borderInset} regionBorderInset={regionBorderInset} />
      </Defs>
      {/* On iOS, nested SVG groups/paths can still consume touches when only
          their outer group disables hit testing. Keep every glow node inert. */}
      <G mask={`url(#${maskId})`} clipPath={`url(#${clipNS}clip-${id})`} pointerEvents="none">
        {INNER_GLOW_PASSES.map(({ depth, opacity }) => (
          // Composite both contours as one opacity pass so corners where they
          // meet do not brighten twice. The mask removes each actual border;
          // their expanded contours start the fade immediately beside it.
          <G key={depth} opacity={opacity} pointerEvents="none">
            <Path d={shape} fill="none" stroke={color}
              strokeWidth={2 * (borderInset + glowWidth * depth)}
              strokeLinejoin="miter" pointerEvents="none" />
            {regionShape && (
              <Path d={regionShape} fill="none" stroke={color}
                strokeWidth={2 * (regionBorderInset + glowWidth * depth)}
                strokeLinejoin="round" pointerEvents="none" />
            )}
          </G>
        ))}
      </G>
    </G>
  )
}

export function TargetHaloLayer({
  targetCircles,
  markerCircles,
  territoryIds,
  settledTerritoryIds = [],
  sourceTerritoryId,
  sourceColor = '#ffffff',
  territoryShapeById,
  territoryRegionShapeById,
  clipNS,
  borderInset,
  regionBorderInset,
  glowWidth,
}: TargetHaloLayerProps) {
  const settledIds = new Set(settledTerritoryIds)
  const openIds = territoryIds.filter((id) => !settledIds.has(id) && id !== sourceTerritoryId)
  if (!targetCircles && glowWidth <= 0) return null

  const renderGlow = (id: string) => {
    if (targetCircles) {
      if (id === sourceTerritoryId) return null
      const position = targetCircles.positionById[id]
      return position ? (
        <Circle key={id} testID={`selection-circle-${id}`}
          cx={position.x} cy={position.y} r={targetCircles.radius}
          fill="#ffffff" fillOpacity={0.18} pointerEvents="none" />
      ) : null
    }
    const shape = territoryShapeById[id]
    if (!markerCircles) return shape ? <InnerGlow key={id} id={id} shape={shape} clipNS={clipNS}
      color={id === sourceTerritoryId ? sourceColor : '#ffffff'} regionShape={territoryRegionShapeById[id]}
      borderInset={borderInset} regionBorderInset={regionBorderInset} glowWidth={glowWidth} /> : null
    const position = markerCircles?.positionById[id]
    const ring = markerCircles ? territoryMarkerSelectionRing(markerCircles.radius, settledIds.has(id)) : undefined
    return shape ? (
      <G key={id} pointerEvents="none">
        <InnerGlow id={id} shape={shape} clipNS={clipNS}
          color={id === sourceTerritoryId ? sourceColor : '#ffffff'}
          regionShape={territoryRegionShapeById[id]}
          borderInset={borderInset} regionBorderInset={regionBorderInset} glowWidth={glowWidth} />
        {position && ring && <G testID={`marker-selection-${id}`} pointerEvents="none">
          <Circle cx={position.x} cy={position.y} r={ring.radius} fill="none"
            stroke="#171717" strokeWidth={ring.strokeWidth} pointerEvents="none" />
        </G>}
      </G>
    ) : null
  }

  return (
    <>
      {sourceTerritoryId && !settledIds.has(sourceTerritoryId) ? renderGlow(sourceTerritoryId) : null}
      <G pointerEvents="none">{openIds.map(renderGlow)}</G>
      <G pointerEvents="none">{settledTerritoryIds.map(renderGlow)}</G>
    </>
  )
}
